import React, { useState } from 'react';
import { Plus, Edit, Trash2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Modal } from '../ui/modal';
import { DivisionOrderAttributeTemplateDto } from '../../types';
import {
  useDivisionOrderAttributeTemplates,
  useDeleteDivisionOrderAttributeTemplate,
} from '../../hooks/use-division-order-attribute-templates';
import { DivisionOrderAttributeTemplateForm } from './division-order-attribute-template-form';

interface DivisionOrderAttributeTemplatesProps {
  divisionId: string;
}

export function DivisionOrderAttributeTemplates({ divisionId }: DivisionOrderAttributeTemplatesProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DivisionOrderAttributeTemplateDto | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<DivisionOrderAttributeTemplateDto | null>(null);

  const { data: templates, isLoading } = useDivisionOrderAttributeTemplates(divisionId);
  const deleteTemplate = useDeleteDivisionOrderAttributeTemplate(divisionId);

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const handleEdit = (template: DivisionOrderAttributeTemplateDto) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingTemplate(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTemplate) return;
    try {
      await deleteTemplate.mutateAsync(deletingTemplate.id);
    } catch (error) {
      console.error('Failed to delete template:', error);
    } finally {
      setDeletingTemplate(null);
    }
  };

  const getFieldTypeColor = (fieldType: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    const colors: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
      string: 'info',
      number: 'success',
      boolean: 'warning',
      date: 'warning',
    };
    return colors[fieldType] || 'default';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const templateList = templates || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary-600" />
            Division Required Attributes
          </h2>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            These attributes are required on every workflow in this division. Workflows inherit them automatically.
          </p>
        </div>
        <Button onClick={handleCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Required Attribute
        </Button>
      </div>

      {/* Form (inline) */}
      {showForm && (
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
          <h3 className="text-base font-medium text-secondary-900 dark:text-white mb-4">
            {editingTemplate ? 'Edit Required Attribute' : 'New Required Attribute'}
          </h3>
          <DivisionOrderAttributeTemplateForm
            divisionId={divisionId}
            template={editingTemplate}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </div>
      )}

      {/* Table */}
      {templateList.length > 0 ? (
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>JSON Path</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Workflows</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templateList
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.displayOrder}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">{template.label}</span>
                        {template.description && (
                          <span className="text-xs text-secondary-500 dark:text-secondary-400">{template.description}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 dark:bg-secondary-700 px-2 py-1 rounded">{template.jsonPath}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getFieldTypeColor(template.fieldType)} size="sm">
                        {template.fieldType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">
                        {template.workflowCount} workflow{template.workflowCount !== 1 ? 's' : ''}
                      </span>
                    </TableCell>
                    <TableCell>
                      {template.isActive ? (
                        <Badge variant="success" size="sm">Active</Badge>
                      ) : (
                        <Badge variant="default" size="sm">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(template)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeletingTemplate(template)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        !showForm && (
          <div className="bg-white dark:bg-secondary-800 rounded-lg border border-dashed border-secondary-300 dark:border-secondary-600 p-12">
            <div className="text-center">
              <ShieldCheck className="h-10 w-10 text-secondary-300 mx-auto mb-3" />
              <p className="text-base font-medium text-secondary-900 dark:text-white mb-1">
                No required attributes
              </p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                Add division-level attributes that will be required on every workflow
              </p>
              <Button onClick={handleCreate} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Required Attribute
              </Button>
            </div>
          </div>
        )
      )}

      {/* Delete Confirmation Modal */}
      {deletingTemplate && (
        <Modal
          isOpen={true}
          onClose={() => setDeletingTemplate(null)}
          title="Delete Required Attribute"
        >
          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-amber-800">This will affect all workflows</h4>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  Deleting <strong>{deletingTemplate.label}</strong> will remove it from{' '}
                  <strong>{deletingTemplate.workflowCount}</strong> workflow
                  {deletingTemplate.workflowCount !== 1 ? 's' : ''}. This cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-secondary-600 dark:text-secondary-400 text-sm">
              Are you sure you want to delete this required attribute?
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setDeletingTemplate(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteConfirm}
                disabled={deleteTemplate.isPending}
              >
                {deleteTemplate.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete Attribute
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
